import { z } from 'zod';
import { partnerListQuerySchema, partnerSortFieldSchema } from './partner.js';
import { productListQuerySchema, productSortFieldSchema } from './product.js';
import { documentsListQuerySchema, documentSortFieldSchema } from './document.js';
import { productBatchListQuerySchema } from './inventory.js';
export const listViewListSchema = z.enum([
    'customers',
    'prospects',
    'suppliers',
    'partners',
    'products',
    'documents',
    'batches',
]);
export const listViewStateSchema = z
    .object({
    filters: z.record(z.string().max(100), z.string()).refine((v) => Object.keys(v).length <= 50),
    sort: z.string().regex(/^[a-zA-Z]+:(asc|desc)$/),
    columns: z
        .array(z.object({ id: z.string().min(1).max(150), visible: z.boolean() }).strict())
        .refine((v) => new Set(v.map((c) => c.id)).size === v.length),
    search: z.string().max(500).optional(),
    presentation: z.enum(['LIST', 'MAP', 'BOARD']).optional(),
    period: z.enum(['THIS_WEEK', 'THIS_MONTH']).optional(),
    fieldRefs: z.record(z.string(), z.string()).default({}),
})
    .strict();
export const listViewWriteSchema = z
    .object({
    list: listViewListSchema,
    name: z.string().trim().min(1).max(100),
    shared: z.boolean(),
    state: listViewStateSchema,
    version: z.number().int().positive().optional(),
})
    .strict();
export const listViewSummarySchema = z.object({
    id: z.string(),
    list: listViewListSchema,
    name: z.string(),
    shared: z.boolean(),
    canEdit: z.boolean(),
    version: z.number().int(),
});
export const listViewIssueSchema = z.object({
    path: z.string(),
    reference: z.string(),
    reason: z.enum(['UNSUPPORTED', 'UNAVAILABLE', 'INVALID']),
});
export const listViewResolutionSchema = z.object({
    effectiveFilters: z.record(z.string()),
    state: listViewStateSchema,
    issues: z.array(listViewIssueSchema),
    view: listViewSummarySchema.nullable(),
});
export const listViewsResponseSchema = z.object({
    data: z.array(listViewSummarySchema),
    defaultId: z.string().nullable(),
    canShare: z.boolean(),
});
export const listViewResolveSchema = z
    .object({ list: listViewListSchema, state: listViewStateSchema })
    .strict();
export const listViewDefaultSchema = z
    .object({ list: listViewListSchema, viewId: z.string().nullable() })
    .strict();
export const listViewVersionSchema = z.object({ version: z.number().int().positive() }).strict();
export const LIST_VIEW_DEFAULT_SORT = {
    customers: 'searchTerm:asc',
    prospects: 'leadScore:desc',
    suppliers: 'searchTerm:asc',
    partners: 'searchTerm:asc',
    products: 'articleNumber:asc',
    documents: 'documentDate:desc',
    batches: 'bestBeforeDate:asc',
};
const partnerFilters = [
    'roles',
    'locationMode',
    'city',
    'postalCode',
    'region',
    'ownerUserId',
    'contactDays',
    'neverContacted',
    'visitDays',
    'addressReview',
    'nearLat',
    'nearLng',
    'radiusKm',
    'priceGroupIds',
    'subdivisionCode',
    'status',
    'industry',
    'industryCode',
    'tags',
    'vatIdStatus',
    'riskStatus',
    'leadStatus',
    'leadSource',
    'supplierRating',
    'countryCode',
    'customFieldFilters',
];
export const LIST_VIEW_FILTERS = {
    customers: partnerFilters,
    prospects: partnerFilters,
    suppliers: partnerFilters,
    partners: partnerFilters,
    products: [
        'status',
        'type',
        'productGroup',
        'tags',
        'isBatchManaged',
        'belowMinStock',
        'expiringWithinDays',
        'activeOnly',
        'customFieldFilters',
    ],
    documents: ['type', 'status', 'paymentStatus', 'dateFrom', 'dateTo', 'openOnly', 'overdueOnly'],
    batches: ['status', 'expiryLevel', 'includeDepleted'],
};
export const LIST_VIEW_COLUMNS = {
    customers: [
        'industryCode',
        'priceGroup',
        'partnerNumber',
        'displayName',
        'debtorAccountNumber',
        'status',
        'riskStatus',
        'paymentTermsDays',
        'creditLimit',
        'email',
        'phone',
        'city',
        'tags',
    ],
    prospects: [
        'industryCode',
        'priceGroup',
        'partnerNumber',
        'displayName',
        'leadStatus',
        'leadScore',
        'dealSizeEstimated',
        'winProbability',
        'expectedClosingDate',
        'leadSource',
        'city',
        'tags',
    ],
    suppliers: [
        'industryCode',
        'priceGroup',
        'partnerNumber',
        'displayName',
        'creditorAccountNumber',
        'supplierRating',
        'minimumOrderValue',
        'deliveryTimeDays',
        'email',
        'phone',
        'city',
        'tags',
    ],
    partners: [
        'industryCode',
        'priceGroup',
        'partnerNumber',
        'displayName',
        'roles',
        'status',
        'riskStatus',
        'industry',
        'email',
        'phone',
        'city',
        'countryCode',
        'tags',
    ],
    products: [
        'select',
        'image',
        'articleNumber',
        'name',
        'productGroup',
        'stock',
        'listPrice',
        'batches',
        'status',
        'purchasePrice',
        'available',
        'tags',
    ],
    documents: ['number', 'type', 'customer', 'documentDate', 'dueDate', 'status', 'grossTotal'],
    batches: [
        'batchNumber',
        'bestBeforeDate',
        'remainingShelfLife',
        'remaining',
        'storageLocation',
        'status',
        'actions',
    ],
};
export function listViewDefaultColumns(list) {
    return LIST_VIEW_COLUMNS[list].map((id) => ({
        id,
        visible: !['priceGroup', 'industryCode'].includes(id),
    }));
}
/** Calendar dates are resolved on use, never persisted in a relative view. */
export function resolveListViewPeriod(period, today) {
    if (period === undefined)
        return {};
    const date = new Date(`${today}T12:00:00Z`);
    if (period === 'THIS_WEEK') {
        date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7));
        const dateFrom = date.toISOString().slice(0, 10);
        date.setUTCDate(date.getUTCDate() + 6);
        return { dateFrom, dateTo: date.toISOString().slice(0, 10) };
    }
    date.setUTCDate(1);
    const dateFrom = date.toISOString().slice(0, 10);
    date.setUTCMonth(date.getUTCMonth() + 1, 0);
    return { dateFrom, dateTo: date.toISOString().slice(0, 10) };
}
export function validateListViewState(list, state) {
    const issues = [];
    if (state.presentation && !['customers', 'prospects', 'suppliers', 'partners'].includes(list))
        issues.push({ path: 'presentation', reference: state.presentation, reason: 'UNSUPPORTED' });
    for (const key of Object.keys(state.filters)) {
        if (!LIST_VIEW_FILTERS[list].includes(key))
            issues.push({ path: `filters.${key}`, reference: key, reason: 'UNSUPPORTED' });
    }
    if (state.period !== undefined &&
        (list !== 'documents' || state.filters['dateFrom'] || state.filters['dateTo'])) {
        issues.push({ path: 'period', reference: state.period, reason: 'INVALID' });
    }
    for (const column of state.columns) {
        if (!LIST_VIEW_COLUMNS[list].includes(column.id) && !column.id.startsWith('cf-'))
            issues.push({ path: `columns.${column.id}`, reference: column.id, reason: 'UNSUPPORTED' });
    }
    const schema = list === 'products'
        ? productListQuerySchema
        : list === 'documents'
            ? documentsListQuerySchema
            : list === 'batches'
                ? productBatchListQuerySchema
                : partnerListQuerySchema;
    const parsed = schema.safeParse({ ...state.filters, sort: state.sort, q: state.search });
    if (!parsed.success)
        for (const issue of parsed.error.issues)
            issues.push({
                path: `filters.${issue.path.join('.')}`,
                reference: String(issue.path[0] ?? ''),
                reason: 'INVALID',
            });
    const field = state.sort.split(':')[0];
    const sortValid = list === 'batches'
        ? ['batchNumber', 'bestBeforeDate', 'remainingQuantity', 'status'].includes(field ?? '')
        : (list === 'products'
            ? productSortFieldSchema
            : list === 'documents'
                ? documentSortFieldSchema
                : partnerSortFieldSchema).safeParse(field).success;
    if (!sortValid)
        issues.push({ path: 'sort', reference: state.sort, reason: 'UNSUPPORTED' });
    return issues;
}
//# sourceMappingURL=list-view.js.map